import { Kundetype, OppdaterProdukt, Ordrebekreftelse, Produkt, ProduktMedTariff, ProduktSpesifikasjon, Vilkaar } from "codegen";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { ZodFormattedError, z } from "zod";

import { faCheck, faFloppyDisk } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Direction, Flex, Justify } from "@ntemarked/mittne-components/dist/Flex/Flex";

import Card from "components/molecules/Card";
import { FullPageLoading } from "components/organisms/FullPageLoading";
import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import { ErrorWrapper, SuccessWrapper } from "components/organisms/form/BaseField";
import { FormElementError } from "components/organisms/form/Form";
import SearchSelectField from "components/organisms/form/SearchSelectField";
import { TextFieldClean } from "components/organisms/form/TextField";
import TextPreviewField from "components/organisms/form/TextPreviewField";
import { ToggleFieldClean } from "components/organisms/form/ToggleField";
import { useStrombestillingApiMutation, useStrombestillingApiQuery } from "hooks/useStrombestillingApi";
import { ProductPageOrderConfirmation } from "pages/Productadmin/ProductPage/ProductPageOrderConfirmation";
import { ProduktTerms } from "pages/Productadmin/ProductPage/ProduktTerms";
import { ProduktUCInfo } from "pages/Productadmin/ProductPage/ProduktUCInfo";
import { noneOrderConfirmation } from "pages/Productadmin/ProductsPage";
import { getDataFromSubmit } from "utils/FormValidator";

export type IndexTerm = Vilkaar & {
	index: number;
};

const productSchema = z.object({
	ucid: z.string().min(1, "Produktet må ha en UC id"),
	agreementType: z.string().min(1, "Produktet må ha en avtaletype"),
	isCompany: z.boolean().optional(),
	orderConfirmation: z.string().min(1, "Ordrebekreftelse må være satt"),
	prices: z.array(z.string().min(1, "Pris må være satt")).optional(),
	name: z.string().min(1),
	description: z.string().min(1, "Produktet må ha beskrivelse"),
	footnote: z.string().optional(),
	priceDescription: z.string().min(1, "Produktet må ha prissammendrag"),
	terms: z.array(z.string().min(1, "Vilkår kan ikke være tomt")).optional(),
});

function getPrices(prices: string[] | undefined, orderConfirmation: Ordrebekreftelse | undefined) {
	if (!orderConfirmation?.prising) return;
	const prising = orderConfirmation.prising;
	return prices?.map((p: string, i: number) => ({
		prisTypeId: prising[i].prisTypeId ?? "",
		gjeldendePris: p,
	}));
}

interface EditProductPageProps {
	products: ProduktSpesifikasjon[];
	orderConfirmations: Ordrebekreftelse[];
}

export default function EditProductPage({ products, orderConfirmations }: Readonly<EditProductPageProps>) {
	// Refs
	const ucIdSearchRef = useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>;
	const tariffAgreementTypeRef = useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>;

	// Main state
	const { tariffId } = useParams<{ tariffId: string }>();
	const product = products.find((p) => p.tariffId === tariffId)!;
	const [selectedUcId, _setSelectedUcId] = useState<string | undefined>(product?.ucProductId ?? undefined);

	// API
	const updateProductMutation = useStrombestillingApiMutation(`/produkter/${product?.tariffId}`, ["products"]);
	const { data: allUcProducts } = useStrombestillingApiQuery<Produkt[]>("/produkter/list-alle", ["products", "uc"]);
	const { data: storedProduct } = useStrombestillingApiQuery<ProduktMedTariff>(
		`/produkter/detaljer/${selectedUcId}`,
		["products", selectedUcId ?? "", "details"],
		selectedUcId !== undefined
	);

	// State
	const unLinkedProducts = allUcProducts?.filter((ucProduct) => !products.some((p) => p.ucProductId === ucProduct.id));
	const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
	const [selectedOrderConfirmation, setSelectedOrderConfirmation] = useState<Ordrebekreftelse | undefined>();
	const [ucProduct, setUcProduct] = useState<Produkt | undefined>();
	const [terms, _setTerms] = useState<IndexTerm[]>([]);
	const [formErrors, setFormErrors] = useState<ZodFormattedError<z.infer<typeof productSchema>>>();
	const [mainError, _setMainError] = useState<string | null>(null);
	const [lastSave, _setLastSave] = useState<Date | null>(null);
	const [productDetails, setProductDetails] = useState<ProduktSpesifikasjon | undefined>();

	// Setters
	function setSelectedUcId(newUcId: string | undefined) {
		_setSelectedUcId(newUcId);
		const newProduct = unLinkedProducts?.find((p) => p.id === newUcId) ?? null;
		if (tariffAgreementTypeRef.current && newProduct?.avtaletype) {
			const agreementTypeInput = tariffAgreementTypeRef.current;
			agreementTypeInput.value = newProduct?.avtaletype;
		}
	}

	function setTerms(terms: IndexTerm[], isFormChanged?: boolean) {
		if (isFormChanged) {
			// To prevent form change to trigger on initial load
			setIsFormChanged(true);
		}
		_setTerms(terms);
	}

	function setMainError(error: string) {
		_setLastSave(null);
		_setMainError(error);
	}

	function setLastSave(save: Date) {
		_setMainError(null);
		_setLastSave(save);
	}

	// useEffects
	useEffect(() => {
		if (selectedUcId) setUcProduct(allUcProducts?.find((p) => p.id === selectedUcId));
	}, [selectedUcId, allUcProducts]);

	useEffect(() => {
		if (storedProduct) setProductDetails(storedProduct?.produktSpesifikasjon);
	}, [storedProduct]);

	useEffect(() => {
		const vilkaar = ucProduct?.vilkaar;
		if (vilkaar) {
			setTerms(
				vilkaar.map((v, i: number) => {
					return { ...v, index: i };
				}),
				false
			);
		}
	}, [ucProduct]);

	useEffect(() => {
		if (product && orderConfirmations?.length) {
			if (product.ordrebekreftelse === noneOrderConfirmation.id) {
				setSelectedOrderConfirmation(noneOrderConfirmation);
				return;
			}
			const defaultOrderConfirmation = orderConfirmations.find((c) => c.id === product.ordrebekreftelse);
			setSelectedOrderConfirmation(defaultOrderConfirmation);
		}
	}, [product, orderConfirmations]);

	useEffect(() => {
		function handlePageUnload(e: BeforeUnloadEvent) {
			e.preventDefault();
		}
		function handleBackNavigation(e: PopStateEvent) {
			e.preventDefault();
			const doGoBack = window.confirm("Er du sikker på at du ønsker å gå tilbake? Det kan hende endringene dine ikke er lagret.");
			if (!doGoBack) {
				window.history.go(1); // This still wipes the page state, but unsure how else to handle it
			}
		}
		if (isFormChanged) {
			window.addEventListener("beforeunload", handlePageUnload);
			window.addEventListener("popstate", handleBackNavigation);
		}
		return () => {
			window.removeEventListener("beforeunload", handlePageUnload);
			window.removeEventListener("popstate", handleBackNavigation);
		};
	}, [isFormChanged]);

	if (allUcProducts === undefined) {
		return <FullPageLoading />;
	}

	return (
		<Flex justifyContent={Justify.start} gap="4rem">
			<div style={{ width: "43.75rem" }}>
				<MainLayoutPageHeader title={product.navn} />
				{(product.updated?.name && product.updated?.at && (
					<p>{`Sist redigert av: ${product.updated?.name}, den ${format(new Date(product.updated?.at), "d. MMMM yyyy, HH:mm", {
						locale: nb,
					})}`}</p>
				)) ?? <p>Produktet er ikke redigert tidligere</p>}

				<StyledForm
					onSubmit={async (e) => {
						e.preventDefault();
						if (updateProductMutation.isPending) {
							return;
						}
						if (ucProduct?.harOrdrebekreftelseIngenIUC && selectedOrderConfirmation?.id !== noneOrderConfirmation.id) {
							const doSaveProduct = window.confirm(
								'Er du sikker på at du ønsker å lagre produktet? Produktet er blokkert for generering av ordrebekreftelser i UC. Fjern ekstrafeltet "ordrebekreftelse" fra produktet og oppdater før du går videre.'
							);
							if (!doSaveProduct) {
								return;
							}
						}
						const validated = getDataFromSubmit(e.target, productSchema);
						if (!validated.success) {
							const parsedErrors = validated.error.format();
							setFormErrors(parsedErrors);
							setMainError("Det er feil i skjemaet, vennligst se over.");
							return;
						}
						setFormErrors(undefined);

						const data = validated.data;
						if (data.orderConfirmation === noneOrderConfirmation.id) {
							if (!window.confirm("Sikker på at produktet ikke skal ha ordrebekreftelse?")) {
								setMainError("Produktet er ikke lagret.");
								return;
							}
						}

						const submitData: OppdaterProdukt = {
							ucProduktId: selectedUcId ?? "",
							avtaleType: data.agreementType,
							kundeType: data.isCompany ? Kundetype.Bedrift : Kundetype.Forbruker,
							beskrivelse: data.description,
							fotnotetekst: data.footnote,
							oppsummering: data.priceDescription,
							vilkaar: data.terms ?? [],
							ordrebekreftelsesmal: data.orderConfirmation,
							priser: getPrices(data.prices, selectedOrderConfirmation),
						};
						const response = await updateProductMutation.mutateAsync(submitData);

						if (!response.ok) {
							setMainError(await response.text());
						} else {
							setIsFormChanged(false);
							setLastSave(new Date());
						}
					}}
					onChange={() => {
						setIsFormChanged(true);
					}}
				>
					<Flex direction={Direction.column} gap="1rem">
						<h2>Avtaleinfo</h2>
						<h3>Generell info som må fylles for nye produkter</h3>
						<TextFieldClean name="tariffid" label="TariffId" defaultValue={product.tariffId} readOnly />
						<SearchSelectField
							name="ucid"
							label="UC produkt"
							inputRef={ucIdSearchRef}
							selectedValue={ucProduct?.navn ?? ""}
							setSelectedValue={setSelectedUcId}
							options={unLinkedProducts
								?.map((p) => ({
									value: p.id,
									label: p.navn ?? "Ukjent",
								}))
								.sort((a, b) => a.label.localeCompare(b.label))}
							required
							readOnly={!!product.ucProductId} // Existing products should not change tariffId
							formErrors={formErrors as FormElementError<unknown>}
						/>
						<TextFieldClean
							name="agreementType"
							label="Avtaletype"
							inputRef={tariffAgreementTypeRef}
							defaultValue={product.avtaletype}
							required
							description='For eksempel "Spotpris time for time", "Spotpris time for time med bindingstid" eller "Tilleggstjeneste med månedsabonnement"'
							formErrors={formErrors as FormElementError<unknown>}
						/>
						<ToggleFieldClean name="isCompany" label="Avtalen er en bedriftsavtale" defaultChecked={product.kundetype === Kundetype.Bedrift} />
					</Flex>

					<ProductPageOrderConfirmation
						orderConfirmations={orderConfirmations}
						selectedOrderConfirmation={selectedOrderConfirmation}
						setSelectedOrderConfirmation={setSelectedOrderConfirmation}
						formErrors={formErrors as FormElementError<unknown>}
						product={product}
					/>

					<Flex direction={Direction.column} gap="1rem">
						<h2>NTE.no</h2>
						<h3>Informasjonen her blir sendt videre og brukt på nte.no</h3>
						<TextFieldClean name="name" label="Produktnavn" value={ucProduct?.navn ?? ""} readOnly={true} />
						<TextPreviewField
							name="description"
							label="Beskrivelse"
							description="Lenker må ha dette formatet: {link:<url>|tittel:<tittel>|tekst:<tekst>}"
							defaultValue={storedProduct?.produkt?.beskrivelse}
							formErrors={formErrors as FormElementError<unknown>}
						/>
						<TextPreviewField
							name="footnote"
							label="Fotnote"
							defaultValue={storedProduct?.produkt?.fotnote?.skript}
							description="Lenker må ha dette formatet: {link:<url>|tittel:<tittel>|tekst:<tekst>}"
							formErrors={formErrors as FormElementError<unknown>}
						/>
						<TextFieldClean
							name="priceDescription"
							label="Prissammendrag"
							defaultValue={storedProduct?.produkt?.prisbeskrivelse}
							// Tvinger rerender når defaultValue oppdateres, som den vil kunne gjøre når noe hentes async
							key={String(storedProduct?.produkt?.prisbeskrivelse)}
							formErrors={formErrors as FormElementError<unknown>}
						/>
						<ProduktTerms terms={terms} setTerms={setTerms} formErrors={formErrors as FormElementError<unknown>} />
					</Flex>

					<Flex direction={Direction.column} gap="1rem">
						{(mainError ?? lastSave) && !updateProductMutation.isPending && (
							<MessageWrapper>
								{mainError && <ErrorWrapper>Produktet ble ikke lagret: {mainError}</ErrorWrapper>}
								{lastSave && (
									<SuccessWrapper>
										<FontAwesomeIcon icon={faCheck} style={{ marginRight: "0.5rem" }} />
										Produktet ble sist lagret {format(lastSave, "d. MMMM yyyy, HH:mm", { locale: nb })}
									</SuccessWrapper>
								)}
							</MessageWrapper>
						)}
						<StyledButton>
							{updateProductMutation.isPending ? (
								<>Laster...</>
							) : (
								<>
									<FontAwesomeIcon icon={faFloppyDisk} style={{ marginRight: "0.5rem" }} />
									Lagre produkt
								</>
							)}
						</StyledButton>
					</Flex>
				</StyledForm>
			</div>

			{storedProduct && (
				<Flex gap="2rem" direction={Direction.column}>
					{storedProduct.produkt?.utilityCloudPriser && storedProduct.produkt.utilityCloudPriser?.length > 0 && (
						<ProduktUCInfo product={storedProduct.produkt} />
					)}

					{productDetails && (
						<Card title="Tariffinformasjon fra strømbestilling-api">
							<PriceInformation>
								<ul>
									<li>Alias: {(productDetails.alias?.length ?? 0) > 0 ? productDetails.alias?.join(", ") : "-"}</li>
									<li>Regler: {(productDetails.regler?.length ?? 0) > 0 ? productDetails.regler?.join(", ") : "-"}</li>
									<li>Hovedprodukt: {productDetails.hovedprodukt !== null ? (productDetails.hovedprodukt ? "Ja" : "Nei") : "-"}</li>
									<li>Kredittsjekk: {productDetails.kredittsjekk !== null ? (productDetails.kredittsjekk ? "Ja" : "Nei") : "-"}</li>
									<li>Manuell: {productDetails.manuell !== null ? (productDetails.manuell ? "Ja" : "Nei") : "-"}</li>
								</ul>
							</PriceInformation>
						</Card>
					)}
				</Flex>
			)}
		</Flex>
	);
}

const StyledForm = styled.form`
	display: flex;
	flex-direction: column;
	gap: 4rem;
`;

const MessageWrapper = styled.div`
	width: 0;
	min-width: 100%;
`;

const StyledButton = styled.button`
	width: fit-content;
`;

export const PriceInformation = styled.div`
	display: flex;
	flex-direction: column;
`;
